import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CallToAction = () => {
  const actionCards = [
    {
      id: 'portfolio',
      title: 'Explore Projects',
      description: 'Dive into the project dimensions and witness cutting-edge innovations in action.',
      icon: 'Layers',
      color: 'brand-cyan',
      link: '/project-dimensions-portfolio',
      buttonText: 'Enter Portfolio',
      gradient: 'from-brand-cyan/20 to-brand-purple/20'
    },
    {
      id: 'learn',
      title: 'Learn About Me',
      description: 'Explore background, philosophy, and timeline on the Identity Matrix page.',
      icon: 'User',
      color: 'brand-green',
      link: '/identity-matrix-about',
      buttonText: 'View Identity',
      gradient: 'from-brand-green/20 to-brand-cyan/20'
    },
    {
      id: 'contact',
      title: 'Connect & Collaborate',
      description: 'Ready to build something extraordinary? Let\'s discuss your next digital transformation.',
      icon: 'MessageSquare',
      color: 'brand-purple',
      link: '/neural-network-contact-collaboration',
      buttonText: 'Get In Touch',
      gradient: 'from-brand-purple/20 to-brand-orange/20'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section className="py-20 bg-background relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        {/* Gradient Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-cyan/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-purple/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-3/4 left-3/4 w-64 h-64 bg-brand-green/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Neural Network Lines */}
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 1000 1000">
          <defs>
            <linearGradient id="neural-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="currentColor" className="text-brand-cyan" />
              <stop offset="50%" stopColor="currentColor" className="text-brand-purple" />
              <stop offset="100%" stopColor="currentColor" className="text-brand-green" />
            </linearGradient>
          </defs>
          <path
            d="M100,200 Q300,100 500,200 T900,200"
            stroke="url(#neural-gradient)"
            strokeWidth="2"
            fill="none"
            opacity="0.5"
          >
            <animate
              attributeName="stroke-dasharray"
              values="0,1000;1000,0;0,1000"
              dur="8s"
              repeatCount="indefinite"
            />
          </path>
          <path
            d="M100,500 Q300,400 500,500 T900,500"
            stroke="url(#neural-gradient)"
            strokeWidth="2"
            fill="none"
            opacity="0.3"
          >
            <animate
              attributeName="stroke-dasharray"
              values="1000,0;0,1000;1000,0"
              dur="10s"
              repeatCount="indefinite"
            />
          </path>
          <path
            d="M100,800 Q300,700 500,800 T900,800"
            stroke="url(#neural-gradient)"
            strokeWidth="2"
            fill="none"
            opacity="0.4"
          >
            <animate
              attributeName="stroke-dasharray"
              values="0,1000;1000,0;0,1000"
              dur="12s"
              repeatCount="indefinite"
            />
          </path>
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-morphism cyber-border mb-6">
            <Icon name="Rocket" size={16} className="text-brand-orange" />
            <span className="font-mono text-sm text-brand-orange">INITIATE SEQUENCE</span>
          </div>
          
          <h2 className="font-orbitron font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Ready to <span className="text-brand-orange">Launch</span>?
          </h2>
          
          <p className="font-inter text-lg text-text-secondary max-w-2xl mx-auto">
            Choose your path into the digital frontier. Whether you're seeking innovation, learning, or collaboration, 
            your journey begins here.
          </p>
        </motion.div>

        {/* Action Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          {actionCards.map((card) => (
            <motion.div
              key={card.id}
              variants={cardVariants}
              className="group relative"
            >
              <div className="relative h-full">
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300`}></div>
                
                {/* Card Content */}
                <div className="relative glass-morphism rounded-2xl p-8 cyber-border hover:cyber-border-active transition-all duration-300 cyber-hover h-full flex flex-col">
                  {/* Icon */}
                  <div className="mb-6">
                    <div className={`w-16 h-16 rounded-xl bg-${card.color}/10 flex items-center justify-center group-hover:cyber-glow transition-all duration-300`}>
                      <Icon 
                        name={card.icon} 
                        size={32} 
                        className={`text-${card.color} group-hover:scale-110 transition-transform duration-300`} 
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 mb-6">
                    <h3 className="font-orbitron font-bold text-xl text-foreground mb-3">
                      {card.title}
                    </h3>
                    <p className="font-inter text-text-secondary leading-relaxed">
                      {card.description}
                    </p>
                  </div>

                  {/* CTA Button */}
                  <Link to={card.link} className="block">
                    <Button
                      variant="outline"
                      fullWidth
                      className={`cyber-border hover:cyber-border-active hover:cyber-glow transition-all duration-300 group-hover:text-${card.color}`}
                      iconName="ArrowRight"
                      iconPosition="right"
                      iconSize={16}
                    >
                      {card.buttonText}
                    </Button>
                  </Link>

                  {/* Status Indicator */}
                  <div className="absolute top-4 right-4 flex items-center space-x-1">
                    <div className={`w-2 h-2 bg-${card.color} rounded-full animate-pulse`}></div>
                    <span className="font-mono text-xs text-text-tertiary">ACTIVE</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="text-center"
        >
          <div className="glass-morphism rounded-2xl p-8 cyber-border max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-left">
                <h3 className="font-orbitron font-bold text-2xl text-foreground mb-2">
                  Ready for <span className="text-brand-cyan">Digital Transformation</span>?
                </h3>
                <p className="font-inter text-text-secondary">
                  Let's build something extraordinary together. Your vision, powered by cutting-edge technology.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 shrink-0">
                <Link to="/neural-network-contact-collaboration">
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-gradient-to-r from-brand-cyan to-brand-purple hover:from-brand-purple hover:to-brand-cyan cyber-glow-intense transition-all duration-500"
                    iconName="MessageSquare"
                    iconPosition="left"
                    iconSize={20}
                  >
                    Start Project
                  </Button>
                </Link>
                
                <Link to="/identity-matrix-about">
                  <Button
                    variant="outline"
                    size="lg"
                    className="cyber-border hover:cyber-border-active hover:cyber-glow transition-all duration-300"
                    iconName="User"
                    iconPosition="left"
                    iconSize={20}
                  >
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CallToAction;